import { useState, useCallback, useEffect } from 'react';
import { Droplets, Check, Info, ThermometerSun, Clock } from 'lucide-react';
import LabBench from '../../LabBench';
import { Dropper, HotPlate } from '@/components/lab';
import InfoBox from '../../InfoBox';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Sulfate Solution Preparation',
    content: `Sulfate (SO4 2-) is a polyatomic anion with tetrahedral geometry. The sulfur atom is bonded to four oxygen atoms with resonance-stabilized double bonds. Sulfates are generally soluble in water, with notable exceptions including BaSO₄, PbSO₄, and CaSO₄. We exploit BaSO₄'s extreme insolubility (Ksp = 1.1x10^-10) for the confirmation test.`
  },
  2: {
    title: 'Barium Chloride Precipitation',
    content: `Adding BaCl₂-CaCl₂ mixture introduces Ba2+ ions that immediately react with sulfate: Ba2+ + SO4 2- -> BaSO₄(s). The white precipitate forms instantly because BaSO₄ is one of the most insoluble salts known. The CaCl₂ in the mixture helps prevent co-precipitation of other slightly soluble barium compounds.`
  },
  3: {
    title: 'Water Bath Heating',
    content: `Heating at 90C serves two purposes: it helps the precipitate particles grow larger (Ostwald ripening) and ensures complete precipitation. Larger particles are easier to observe and settle faster. The 10-minute heating period allows the fine precipitate to coagulate into visible clumps, making the test result clearer.`
  },
  4: {
    title: 'The Digestion Process',
    content: `During "digestion," small precipitate particles dissolve and reprecipitate onto larger particles (Ostwald ripening). This thermodynamic process occurs because smaller particles have higher surface energy. The result is fewer but larger, more well-defined crystals. Proper digestion gives cleaner precipitates and more reliable test results.`
  },
  5: {
    title: 'Observing Barium Sulfate',
    content: `The white BaSO₄ precipitate should appear dense and well-settled after heating. Its brilliant white color and heavy, powdery texture are characteristic. However, other barium salts (BaCO₃, BaSO₃) can also appear white, so the acid stability test is essential to confirm true sulfate presence.`
  },
  6: {
    title: 'The Acid Stability Test',
    content: `Adding 6M HCl tests whether the precipitate is truly BaSO₄ or another barium salt. This is crucial because BaCO₃ (from carbonate) and BaSO₃ (from sulfite) both dissolve in HCl: BaCO₃ + 2HCl -> BaCl₂ + H₂O + CO₂. In contrast, BaSO₄ is completely insoluble in dilute acids - this is the definitive confirmation.`
  },
  7: {
    title: 'Heating with Acid',
    content: `Heating accelerates any potential dissolution reaction. If the precipitate were BaCO₃ or BaSO₃, it would dissolve with effervescence (CO₂ or SO₂ release). Hot acid provides the maximum driving force for dissolution. If the precipitate remains after boiling in HCl, it must be BaSO₄.`
  },
  8: {
    title: 'Confirming Precipitate Stability',
    content: `BaSO₄'s resistance to acid dissolution is remarkable and diagnostic. The sulfate ion is an extremely weak base (conjugate base of strong acid H₂SO₄), so it cannot be protonated by HCl. The intact precipitate after acid treatment proves unambiguously that sulfate was present in the original solution.`
  },
  9: {
    title: 'Recording Positive Sulfate Result',
    content: `A positive sulfate test requires: (1) white precipitate with BaCl₂, and (2) NO dissolution in hot HCl. Both criteria must be met because carbonate and sulfite could give false positives in step 1 alone. The acid stability test makes the barium sulfate test highly specific and reliable for sulfate confirmation.`
  }
};

interface Part5SulfateProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

export default function Part5Sulfate({ onStepComplete, currentStep = 1 }: Part5SulfateProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [so4SolutionAdded, setSo4SolutionAdded] = useState(false);
  const [bacl2Added, setBacl2Added] = useState(false);
  const [isDropping, setIsDropping] = useState(false);
  const [inWaterBath, setInWaterBath] = useState(false);
  const [heatingProgress, setHeatingProgress] = useState(0);
  const [heatingComplete, setHeatingComplete] = useState(false);
  const [baso4Observed, setBaso4Observed] = useState(false);
  const [hclAdded, setHclAdded] = useState(false);
  const [heatedWithHcl, setHeatedWithHcl] = useState(false);
  const [precipitateStable, setPrecipitateStable] = useState(false);
  const [resultRecorded, setResultRecorded] = useState(false);

  // Heating timer effect
  useEffect(() => {
    if (inWaterBath && !heatingComplete && heatingProgress > 0 && heatingProgress < 100) {
      const interval = setInterval(() => {
        setHeatingProgress(prev => {
          const newProgress = prev + 1;
          if (newProgress >= 100) {
            setHeatingComplete(true);
            if (onStepComplete) onStepComplete('sulfate-4');
          }
          return Math.min(newProgress, 100);
        });
      }, 60); // About 6 seconds total
      return () => clearInterval(interval);
    }
  }, [inWaterBath, heatingComplete, heatingProgress, onStepComplete]);

  const getPrecipitateAmount = () => {
    if (!bacl2Added) return 0;
    if (!heatingComplete) return 30 + heatingProgress * 0.5;
    return 80;
  };

  const handleAddSO4Solution = useCallback(() => {
    setSo4SolutionAdded(true);
    if (onStepComplete) onStepComplete('sulfate-1');
  }, [onStepComplete]);

  const handleAddBaCl2 = useCallback(() => {
    if (so4SolutionAdded) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setBacl2Added(true);
        if (onStepComplete) onStepComplete('sulfate-2');
      }, 500);
    }
  }, [so4SolutionAdded, onStepComplete]);

  const handlePlaceInWaterBath = useCallback(() => {
    if (bacl2Added) {
      setInWaterBath(true);
      setHeatingProgress(1);
      if (onStepComplete) onStepComplete('sulfate-3');
    }
  }, [bacl2Added, onStepComplete]);

  const handleObserveBaSO4 = useCallback(() => {
    if (heatingComplete) {
      setBaso4Observed(true);
      if (onStepComplete) onStepComplete('sulfate-5');
    }
  }, [heatingComplete, onStepComplete]);

  const handleAddHCl = useCallback(() => {
    if (baso4Observed) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setHclAdded(true);
        if (onStepComplete) onStepComplete('sulfate-6');
      }, 500);
    }
  }, [baso4Observed, onStepComplete]);

  const handleHeatWithHCl = useCallback(() => {
    if (hclAdded) {
      setHeatedWithHcl(true);
      if (onStepComplete) onStepComplete('sulfate-7');
    }
  }, [hclAdded, onStepComplete]);

  const handleConfirmStability = useCallback(() => {
    if (heatedWithHcl) {
      setPrecipitateStable(true);
      if (onStepComplete) onStepComplete('sulfate-8');
    }
  }, [heatedWithHcl, onStepComplete]);

  const handleRecordResult = useCallback(() => {
    if (precipitateStable) {
      setResultRecorded(true);
      if (onStepComplete) onStepComplete('sulfate-9');
    }
  }, [precipitateStable, onStepComplete]);

  return (
    <LabBench
      experimentName="Sulfate Confirmation Test"
      completedCount={[so4SolutionAdded, bacl2Added, inWaterBath, heatingComplete, baso4Observed, hclAdded, heatedWithHcl, precipitateStable, resultRecorded].filter(Boolean).length}
      totalSteps={9}
      equipment={['SO4 solution', 'BaCl₂-CaCl₂', 'HCl', 'Water bath', 'Hot plate']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="sulfate-test">
        {/* Equipment Area */}
        <div className="equipment-row">
          {/* Test Tube with precipitate */}
          <div className="equipment-section">
            <svg viewBox="0 0 70 150" width={70} height={150}>
              <defs>
                <linearGradient id="tube-glass-so4" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                  <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                </linearGradient>
              </defs>

              {/* Test tube rim */}
              <ellipse cx="35" cy="15" rx="16" ry="5" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Test tube body */}
              <path d="M19 15 L19 115 Q19 130 35 130 Q51 130 51 115 L51 15" fill="url(#tube-glass-so4)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Liquid fill */}
              {so4SolutionAdded && (
                <path
                  d="M21 45 L21 113 Q21 127 35 127 Q49 127 49 113 L49 45 Z"
                  fill="#F5F5F5"
                  opacity="0.7"
                />
              )}

              {/* BaSO₄ white precipitate */}
              {bacl2Added && (
                <g>
                  {/* Main precipitate mass */}
                  <ellipse
                    cx="35"
                    cy="115"
                    rx={8 + getPrecipitateAmount() / 10}
                    ry={2 + getPrecipitateAmount() / 25}
                    fill="#ECEFF1"
                    stroke="#CFD8DC"
                    strokeWidth="0.5"
                  />

                  {/* Floating particles settling */}
                  {!heatingComplete && (
                    <>
                      <ellipse cx="28" cy={90 + heatingProgress * 0.2} rx="3" ry="1.5" fill="#ECEFF1" opacity="0.8" />
                      <ellipse cx="42" cy={85 + heatingProgress * 0.25} rx="2.5" ry="1" fill="#ECEFF1" opacity="0.7" />
                      <ellipse cx="35" cy={80 + heatingProgress * 0.3} rx="4" ry="2" fill="#ECEFF1" opacity="0.6" />
                    </>
                  )}

                  {/* Dense settled precipitate after heating */}
                  {heatingComplete && (
                    <>
                      <ellipse cx="30" cy="110" rx="5" ry="2" fill="#ECEFF1" />
                      <ellipse cx="40" cy="108" rx="4" ry="1.5" fill="#ECEFF1" />
                    </>
                  )}
                </g>
              )}

              {/* Glass shine */}
              <line x1="23" y1="20" x2="23" y2="105" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="equipment-label">SO4 Solution</span>
          </div>

          {/* Water Bath indicator */}
          {inWaterBath && (
            <div className="equipment-section water-bath">
              <svg viewBox="0 0 100 80" width={100} height={80}>
                <defs>
                  <linearGradient id="water-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#81D4FA" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#4FC3F7" stopOpacity="0.8" />
                  </linearGradient>
                </defs>

                {/* Container */}
                <rect x="10" y="10" width="80" height="50" fill="url(#water-grad)" stroke="#29B6F6" strokeWidth="2" rx="5" />

                {/* Temperature indicator */}
                <text x="50" y="40" fontSize="12" fill="#0277BD" textAnchor="middle" fontWeight="bold">90C</text>

                {/* Steam bubbles */}
                {heatingProgress > 0 && !heatingComplete && (
                  <g opacity="0.5">
                    {[...Array(4)].map((_, i) => (
                      <circle key={i} cx={30 + i * 15} cy={5} r={2}>
                        <animate
                          attributeName="cy"
                          values="10;0;-5"
                          dur={`${0.5 + i * 0.1}s`}
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          values="0.5;0.3;0"
                          dur={`${0.5 + i * 0.1}s`}
                          repeatCount="indefinite"
                        />
                      </circle>
                    ))}
                  </g>
                )}

                {/* Label */}
                <text x="50" y="72" fontSize="9" fill="var(--text-secondary)" textAnchor="middle">Water Bath</text>
              </svg>
            </div>
          )}

          {/* BaCl₂ Dropper */}
          <div className="equipment-section">
            <Dropper
              label="BaCl₂-CaCl₂"
              liquidColor="#F5F5F5"
              onClick={so4SolutionAdded && !bacl2Added ? handleAddBaCl2 : undefined}
              active={so4SolutionAdded && !bacl2Added}
              dropping={isDropping && !bacl2Added}
              disabled={!so4SolutionAdded || bacl2Added}
            />
          </div>

          {/* HCl Dropper */}
          {baso4Observed && !hclAdded && (
            <div className="equipment-section">
              <Dropper
                label="6M HCl"
                liquidColor="#F5F5F5"
                onClick={handleAddHCl}
                active={true}
                dropping={isDropping}
              />
            </div>
          )}
        </div>

        {/* Heating Progress */}
        {inWaterBath && !heatingComplete && (
          <div className="heating-progress">
            <ThermometerSun size={16} />
            <span>Heating at 90C...</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${heatingProgress}%` }} />
            </div>
            <span>{Math.round(heatingProgress * 0.6)}/60 sec</span>
          </div>
        )}

        {/* Status Messages */}
        <div className="status-section">
          {!so4SolutionAdded && (
            <div className="instruction-card">
              <span className="step-badge">Step 1</span>
              <p>Add 3 mL of <strong>sulfate solution</strong> to a clean test tube.</p>
              <button className="action-btn" onClick={handleAddSO4Solution}>
                <Droplets size={16} />
                Add SO4 Solution
              </button>
            </div>
          )}

          {so4SolutionAdded && !bacl2Added && (
            <div className="instruction-card">
              <span className="step-badge">Step 2</span>
              <p>Add <strong>0.5 mL of BaCl₂-CaCl₂ mixture</strong>.</p>
              <p className="hint">Click the BaCl₂-CaCl₂ dropper.</p>
            </div>
          )}

          {bacl2Added && !inWaterBath && (
            <div className="instruction-card">
              <span className="step-badge">Step 3</span>
              <p>Place test tube in <strong>90C water bath</strong>.</p>
              <button className="action-btn" onClick={handlePlaceInWaterBath}>
                <ThermometerSun size={16} />
                Place in Water Bath
              </button>
            </div>
          )}

          {inWaterBath && !heatingComplete && (
            <div className="instruction-card">
              <span className="step-badge">Step 4</span>
              <p>Wait for heating to complete (<strong>~10 minutes</strong> simulated).</p>
              <p className="hint">The white BaSO₄ precipitate is forming and coagulating...</p>
            </div>
          )}

          {heatingComplete && !baso4Observed && (
            <div className="instruction-card">
              <span className="step-badge">Step 5</span>
              <p>Observe the <strong>white BaSO₄ precipitate</strong>.</p>
              <button className="action-btn" onClick={handleObserveBaSO4}>
                <Check size={16} />
                Observe Precipitate
              </button>
            </div>
          )}

          {baso4Observed && !hclAdded && (
            <div className="instruction-card">
              <span className="step-badge">Step 6</span>
              <p>Add <strong>5 drops of 6M HCl</strong> to the precipitate.</p>
              <p className="hint">Click the HCl dropper. BaSO₄ should NOT dissolve in acid.</p>
            </div>
          )}

          {hclAdded && !heatedWithHcl && (
            <div className="instruction-card">
              <span className="step-badge">Step 7</span>
              <p><strong>Heat</strong> the tube in boiling water bath.</p>
              <button className="action-btn" onClick={handleHeatWithHCl}>
                <ThermometerSun size={16} />
                Heat with HCl
              </button>
            </div>
          )}

          {heatedWithHcl && !precipitateStable && (
            <div className="instruction-card">
              <span className="step-badge">Step 8</span>
              <p>Confirm the precipitate <strong>does NOT dissolve</strong> in HCl.</p>
              <button className="action-btn" onClick={handleConfirmStability}>
                <Check size={16} />
                Confirm Stability
              </button>
            </div>
          )}

          {precipitateStable && !resultRecorded && (
            <div className="instruction-card success">
              <span className="step-badge">Step 9</span>
              <p>Record the <strong>positive result</strong> for sulfate confirmation.</p>
              <button className="action-btn success" onClick={handleRecordResult}>
                <Check size={16} />
                Record Result
              </button>
            </div>
          )}

          {resultRecorded && (
            <div className="result-card">
              <Check size={20} />
              <div>
                <p><strong>SO4 2- Confirmed!</strong></p>
                <p>The white BaSO₄ precipitate that does NOT dissolve in HCl confirms sulfate ions.</p>
              </div>
            </div>
          )}
        </div>

        {/* Key observation */}
        {hclAdded && (
          <div className="observation-note">
            <Info size={16} />
            <span>
              <strong>Key observation:</strong> BaSO₄ does NOT dissolve in HCl.
              This distinguishes sulfate from other white barium precipitates like BaCO₃ (which dissolves in acid).
            </span>
          </div>
        )}

        {/* Theory Box */}
        <div className="theory-box">
          <h4><Info size={14} /> Barium Sulfate Precipitation</h4>
          <p>
            Barium sulfate (BaSO₄) is one of the most insoluble salts known, with Ksp = 1.1 x 10^-10.
            The key confirmation is that BaSO₄ does NOT dissolve in HCl, unlike BaCO₃ which would dissolve with effervescence.
          </p>
          <div className="equation">
            Ba<sup>2+</sup> + SO<sub>4</sub><sup>2-</sup> → BaSO<sub>4</sub>(s) ↓ (white)
          </div>
          <p className="equation-note">BaSO₄ is insoluble in dilute HCl - this confirms sulfate!</p>
        </div>

        <style>{`
          .sulfate-test {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 650px;
          }

          .equipment-row {
            display: flex;
            gap: 30px;
            align-items: flex-end;
            flex-wrap: wrap;
            justify-content: center;
          }

          .equipment-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .water-bath {
            background: var(--bg-elevated);
            padding: 10px;
            border-radius: 12px;
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .heating-progress {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 20px;
            background: rgba(255, 152, 0, 0.1);
            border-radius: 8px;
            font-size: 13px;
            color: #FF9800;
            width: 100%;
          }

          .progress-bar {
            flex: 1;
            height: 8px;
            background: var(--bg-dark);
            border-radius: 4px;
            overflow: hidden;
          }

          .progress-fill {
            height: 100%;
            background: #FF9800;
            transition: width 0.1s;
          }

          .status-section {
            width: 100%;
            min-height: 100px;
          }

          .instruction-card {
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            border-left: 4px solid var(--primary);
          }

          .instruction-card.success {
            border-left-color: #00A86B;
          }

          .step-badge {
            display: inline-block;
            background: var(--primary);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 8px;
          }

          .instruction-card p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .instruction-card .hint {
            font-size: 12px;
            color: var(--text-muted);
            font-style: italic;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            margin-top: 8px;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .action-btn.success:hover {
            background: #008B5B;
          }

          .result-card {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 16px 20px;
            background: rgba(0, 168, 107, 0.1);
            border-radius: 12px;
            color: #00A86B;
          }

          .result-card p {
            margin: 0;
            color: var(--text-primary);
          }

          .result-card p:first-child {
            color: #00A86B;
            font-weight: 600;
          }

          .observation-note {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            padding: 12px 16px;
            background: rgba(99, 102, 241, 0.1);
            border-radius: 8px;
            font-size: 12px;
            color: var(--primary);
            width: 100%;
          }

          .theory-box {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
          }

          .theory-box h4 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box p {
            margin: 0 0 12px 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }

          .theory-box .equation {
            text-align: center;
            font-size: 14px;
            font-family: 'Times New Roman', serif;
            color: var(--text-secondary);
            background: var(--bg-dark);
            padding: 10px;
            border-radius: 6px;
            margin-bottom: 8px;
          }

          .equation-note {
            font-size: 11px !important;
            color: var(--text-muted) !important;
            text-align: center;
          }

          .info-buttons-row {
            display: flex;
            gap: 8px;
            justify-content: center;
            flex-wrap: wrap;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {/* Info Buttons */}
        <div className="info-buttons-row">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((stepId) => (
            stepInfoContent[stepId] && (
              <button
                key={stepId}
                className="step-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowInfo(stepId); }}
                title={stepInfoContent[stepId].title}
              >
                ?
              </button>
            )
          ))}
        </div>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp3"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
